import { useSearchParams } from 'react-router-dom'
import RestrictedTab from '../RestrictedTab'
import { PostalAddressSection, ShopifySection, StoreFactsSection, StoreTypeSection } from '../StoreSection'
import { STORE_SECTIONS, emptySections, storeSectionFrom } from '../storeSections'
import { useWorkspaceStore } from '../../../stores/workspaceStore'
import { SectionLayout, SectionNav, SectionPanel, useVisitedSections } from '../../../ds'

/**
 * Store Tab — the facts about the shop, and the connection they are read from
 * (App IA v5 · phase 2c, `settings-consolidation.md` §3.6: *"storeContext, domains,
 * Shopify connection"*), laid out to v6 (MAR-386): a section nav and ONE section at a time —
 * Store type · What she may say · Postal address · Shopify connection.
 *
 * ## `?section=` — beside `?tab=`, never instead of it
 *
 * Same contract as Limits (MAR-385): the open section is derived from the URL at the read, with
 * no state mirroring it, so `/authority?tab=store&section=address` lands on the address and Back
 * walks sections. An unknown or missing value opens Store type. `screens/Authority.tsx` drops
 * `section` on a tab switch. A section with nothing stored is marked "Empty" in the nav.
 *
 * ## A section switch never drops a draft
 *
 * Each section sits in a ds `SectionPanel`: mounted the first time it is opened, then kept
 * mounted but `hidden`/`inert` while another is open, so an unsaved fact or address survives a
 * trip to another section and back. A workspace switch drops every hidden section in the same
 * render (`useVisitedSections`' reset key), so none can hold the previous store's draft.
 *
 * ## Permissions — unchanged by the layout
 *
 * The whole tab stays behind `RestrictedTab`, nav included: every section writes through the
 * workspace PUT, which refuses a member, so a member meets one "Access restricted" card.
 */
const StoreTab = () => {
  const currentWorkspace = useWorkspaceStore((state) => state.currentWorkspace)
  const [searchParams, setSearchParams] = useSearchParams()
  const section = storeSectionFrom(searchParams)
  const visited = useVisitedSections(section, currentWorkspace?.id)
  const empty = emptySections(currentWorkspace)

  const open = (id: string) => {
    // Merge, never replace — the same rule as `?tab=` (a receipt link's context survives).
    const next = new URLSearchParams(searchParams)
    next.set('section', id)
    setSearchParams(next)
  }

  const items = STORE_SECTIONS.map((s) => ({ ...s, hint: empty.has(s.id) ? 'Empty' : undefined }))

  return (
    <RestrictedTab>
      <SectionLayout nav={<SectionNav items={items} value={section} onChange={open} label="Store sections" />}>
        <SectionPanel id="type" value={section} visited={visited}><StoreTypeSection /></SectionPanel>
        <SectionPanel id="facts" value={section} visited={visited}><StoreFactsSection /></SectionPanel>
        <SectionPanel id="address" value={section} visited={visited}><PostalAddressSection /></SectionPanel>
        <SectionPanel id="shopify" value={section} visited={visited}><ShopifySection /></SectionPanel>
      </SectionLayout>
    </RestrictedTab>
  )
}

export default StoreTab
